import type {
  OnboardingAnswers,
  OnboardingProposals,
  OnboardingState,
  StepKey,
} from "@desfecha/contracts";
import { api } from "./client";

const base = (slug: string) => `/v1/c/${slug}/onboarding`;
type WithState<T = object> = T & { onboarding: OnboardingState };

export interface EntryMenu {
  mode: "menu";
  message: string;
  options: Array<{ title: string; teamId: string }>;
  afterHours: string | null;
}
export interface EntryTeam {
  mode: "team";
  teamId: string;
  afterHours: string | null;
}
export interface EntryAssistant {
  mode: "assistant";
  assistantId: string;
}

export const onboardingApi = {
  get: (slug: string) => api<WithState>(base(slug)),
  welcome: (slug: string) =>
    api<WithState>(`${base(slug)}/welcome`, { method: "POST", body: {} }),
  answers: (slug: string, answers: OnboardingAnswers) =>
    api<WithState>(`${base(slug)}/answers`, { method: "PUT", body: answers }),
  refine: (slug: string) =>
    api<{
      proposals: OnboardingProposals;
      refined: boolean;
      reason?: string;
    }>(`${base(slug)}/proposals/ai`, { method: "POST", body: {} }),
  skip: (slug: string, step: StepKey, skipped: boolean) =>
    api<WithState>(`${base(slug)}/skip`, {
      method: "PUT",
      body: { step, skipped },
    }),
  solo: (slug: string, solo: boolean) =>
    api<WithState>(`${base(slug)}/solo`, { method: "PUT", body: { solo } }),
  /** The teams without business hours take these (the questionnaire's, when none is given). */
  hours: (slug: string, hoursId?: string) =>
    api<WithState<{ linked: number }>>(`${base(slug)}/hours`, {
      method: "POST",
      body: hoursId ? { hoursId } : {},
    }),
  teams: (slug: string, teams: Array<{ name: string }>) =>
    api<WithState<{ created: number }>>(`${base(slug)}/teams`, {
      method: "POST",
      body: { teams },
    }),
  organization: (
    slug: string,
    body: {
      outcomes: OnboardingProposals["outcomes"];
      snippets: OnboardingProposals["snippets"];
      tags: OnboardingProposals["tags"];
      autoClose: OnboardingProposals["autoClose"] | null;
      /** Outcomes already in the workspace that were unchecked. */
      off?: string[];
    },
  ) =>
    api<
      WithState<{
        applied: {
          outcomes: number;
          snippets: number;
          tags: number;
          autoClose: boolean;
        };
      }>
    >(`${base(slug)}/organization`, { method: "POST", body }),
  entry: (slug: string, body: EntryMenu | EntryTeam | EntryAssistant) =>
    api<WithState<{ flowId: string | null }>>(`${base(slug)}/entry`, {
      method: "POST",
      body,
    }),
  rehearsal: (slug: string) =>
    api<WithState>(`${base(slug)}/rehearsal`, { method: "POST", body: {} }),
  live: (slug: string) =>
    api<WithState>(`${base(slug)}/live`, { method: "POST", body: {} }),
  tour: (slug: string) =>
    api<{ attendant: { seen: boolean; show: boolean } }>(`${base(slug)}/tour`),
  tourDone: (slug: string, outcome: "completed" | "skipped") =>
    api<{ ok: true }>(`${base(slug)}/tour`, {
      method: "PUT",
      body: { kind: "attendant", outcome },
    }),
  tourReset: (slug: string) =>
    api<{ ok: true }>(`${base(slug)}/tour/reset`, { method: "POST", body: {} }),
};
